import React from 'react';
import { FileText } from 'lucide-react';
import SEO from '../components/SEO';

const Terms = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 pb-16">
      <SEO
        title="Terms of Service — Jerusalem Holy Prayer"
        description="The terms and conditions that govern the use of Jerusalem Holy Prayer's pilgrimage delivery service."
        path="/terms"
      />
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full shadow-xl">
                <FileText className="h-12 w-12 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-3">Terms of Service</h1>
            <p className="text-gray-600">Last Updated: May 2026</p>
          </div>

          {/* Content card */}
          <div data-testid="terms-content" className="bg-white rounded-xl shadow-lg p-8 md:p-12 space-y-10">

            <section>
              <p className="text-gray-700 leading-relaxed">
                Welcome to Jerusalem Holy Prayer. By using our website and purchasing our services, you agree
                to the following terms and conditions. Please read them carefully.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 font-serif mb-3">
                1. Independent Service Provider (Non-Affiliation)
              </h2>
              <div className="border-l-4 border-amber-500 bg-amber-50/60 rounded-r-lg pl-5 py-4">
                <p className="text-gray-800 leading-relaxed">
                  Jerusalem Holy Prayer is a private, independent service provider. We are
                  <span className="font-semibold"> not affiliated with, endorsed by, sponsored by, or partnered with </span>
                  the Church of the Holy Sepulchre, the Western Wall Heritage Foundation, the Franciscan
                  Custody of the Holy Land, or any other official religious, ecclesiastical, or governmental
                  authority in Jerusalem. Our service is a private pilgrimage initiative conducted on behalf of
                  our clients.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 font-serif mb-3">
                2. Nature of the Service (What You Are Purchasing)
              </h2>
              <div className="border-l-4 border-amber-500 bg-amber-50/60 rounded-r-lg pl-5 py-4">
                <p className="text-gray-800 leading-relaxed">
                  You are purchasing a <span className="font-semibold">physical delivery service</span>: we print
                  your written prayer on our Weekly Sacred Scroll, personally carry it to the holy sites you
                  select in Jerusalem, and email you a dated photograph as proof of delivery.
                </p>
                <p className="text-gray-800 leading-relaxed mt-3">
                  We <span className="font-semibold">do not</span> sell blessings, promise or guarantee any
                  spiritual, religious, health, financial, emotional, or personal outcomes, and we do not pray
                  on your behalf. No result, sign, feeling, or life event is implied, promised, or warranted
                  as a consequence of purchasing this service.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 font-serif mb-3">3. Description of Service</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                Our service consists of the following logistics:
              </p>
              <ul className="list-disc list-outside space-y-2 text-gray-700 ml-6 mb-4">
                <li>Receiving and printing your prayer on a physical scroll.</li>
                <li>The physical transport of said scroll to the holy sites selected at the time of purchase.</li>
              </ul>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Definition of Delivery</h3>
                  <p className="text-gray-700 leading-relaxed">
                    &ldquo;Delivery&rdquo; is defined as the act of a member of our Jerusalem team carrying the
                    scroll to the site, placing it with reverence, and facilitating physical contact between
                    the scroll and the sacred location (e.g., the Stone of Anointing or the Western Wall).
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Maintenance of Sanctity</h3>
                  <p className="text-gray-700 leading-relaxed">
                    To respect the maintenance and sanctity of the holy sites, physical scrolls are not left
                    permanently on-site to be discarded by custodians. After the delivery and offering are
                    completed, the scrolls remain in our care.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 font-serif mb-3">4. Proof of Delivery</h2>
              <p className="text-gray-700 leading-relaxed">
                We provide &ldquo;Proof of Delivery&rdquo; via a digital photograph sent to the email address provided at
                checkout. This photograph will feature the prayer scroll (identified by a unique reference
                code) at the requested holy site. Once this photograph is sent, the service is considered
                fully rendered and complete.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 font-serif mb-3">5. Site Access and Delays</h2>
              <p className="text-gray-700 leading-relaxed">
                Jerusalem&apos;s holy sites are subject to unannounced closures due to religious ceremonies,
                security protocols, or maintenance. We reserve the right to delay prayer delivery in the event
                of such closures. Prayers will be delivered at the earliest possible opportunity during the
                next available weekly cycle.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 font-serif mb-3">6. Content Guidelines</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to refuse the delivery of any prayer that contains hate speech,
                incitement to violence, or disrespectful language toward the holy sites. If a prayer is
                refused, a full refund will be issued.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 font-serif mb-3">
                7. Refund Policy &amp; Satisfaction Guarantee
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We offer a <span className="font-semibold">30-Day Money-Back Guarantee</span> from the date of
                purchase. Refunds are available at any time within this period, no questions asked. Due to the
                personalized nature of the service, refunds cannot be issued after 30 days once the delivery
                has been physically performed and photographic proof has been sent.
              </p>
            </section>

            <section className="pt-6 border-t border-amber-100">
              <p className="text-gray-700 leading-relaxed mb-2">
                Questions about these terms, refund requests, or any other inquiry? Email us at{' '}
                <a
                  href="mailto:info@jerusalemholyprayer.org"
                  className="text-amber-700 font-semibold hover:text-amber-800 underline underline-offset-2"
                  data-testid="terms-contact-email"
                >
                  info@jerusalemholyprayer.org
                </a>
                . We typically respond within 24 hours.
              </p>
              <p className="text-center text-sm text-amber-700 font-semibold tracking-wide mt-6">
                © 2026 Jerusalem Holy Prayer · Independent Pilgrimage Services
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
